import { useTranslation } from "react-i18next";

/**
 * Shared share-intent row -- previously hand-duplicated between CertificateView.js and
 * StudentCourseCongratulations.js (same five networks, same encodeURIComponent pattern, just
 * copy-pasted). `url` should always be the PUBLIC verify URL, never a private dashboard route --
 * that's what makes a shared link show the same thing to everyone who opens it (the verify page)
 * regardless of who shared it or from where, instead of leaking a student's private "my
 * certificate" view to whoever clicks the link on social media.
 *
 * `certificate` (optional) additionally renders LinkedIn's own "Add to Profile" deep link --
 * distinct from the generic "Share on LinkedIn" post-intent icon below, this opens LinkedIn's
 * own certification-add flow, pre-filled from the certificate's real data (course title, issue
 * date, verify URL as the credential ID's URL) so a student can add it as a real Licenses &
 * Certifications entry on their profile, not just share a link in a post.
 */
const buildLinkedInAddUrl = (certificate, verifyUrl) => {
  if (!certificate) return null;

  const issued = certificate.issued_at ? new Date(certificate.issued_at) : null;
  const params = new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    name: certificate.course_title || "SmartTech Academy Certificate",
    organizationName: "SmartTech Academy",
    certUrl: verifyUrl,
    certId: certificate.certificate_number || "",
  });
  if (issued && !Number.isNaN(issued.getTime())) {
    params.set("issueYear", String(issued.getFullYear()));
    params.set("issueMonth", String(issued.getMonth() + 1));
  }

  return `https://www.linkedin.com/profile/add?${params.toString()}`;
};

const CertificateShareRow = ({ url, text, label, className = "rbt-certificate-share", certificate = null }) => {
  const { t } = useTranslation("certificate");
  const linkedInAddUrl = buildLinkedInAddUrl(certificate, url);
  const resolvedLabel = label ?? t("share.label", "Share this achievement");

  return (
    <div className={className}>
      <span className={`${className}__label`}>{resolvedLabel}</span>

      {linkedInAddUrl && (
        <a
          href={linkedInAddUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${className}__linkedin-add`}
        >
          <i className="feather-linkedin"></i>
          {t("share.addToLinkedIn", "Add to LinkedIn Profile")}
        </a>
      )}

      <div className={`${className}__icons`}>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("share.shareOnFacebook", "Share on Facebook")}
        >
          <i className="feather-facebook"></i>
        </a>
        <a
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("share.shareOnTwitter", "Share on Twitter")}
        >
          <i className="feather-twitter"></i>
        </a>
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("share.shareOnLinkedIn", "Share on LinkedIn")}
        >
          <i className="feather-linkedin"></i>
        </a>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("share.shareOnWhatsApp", "Share on WhatsApp")}
        >
          <i className="feather-message-circle"></i>
        </a>
        <a
          href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("share.shareOnTelegram", "Share on Telegram")}
        >
          <i className="feather-send"></i>
        </a>
      </div>
    </div>
  );
};

export default CertificateShareRow;
